import { action, ActionType } from 'typesafe-actions';
import { Reducer } from 'redux';
import { ThunkResult } from '../../typing/types';
import Geolocation from 'react-native-geolocation-service';
import OldGeolocation from '@react-native-community/geolocation';
import { Platform } from 'react-native';
import moment from 'moment';

export const SET_CACHE_LOCATION = '@geolocation/SET_CACHE_LOCATION';
export const RESET = '@geolocation/RESET';

export type CurrentLocation = {
  mocked: boolean;
  coords: { latitude: number; longitude: number; altitude: number; speed: number };
  timestamp: number;
  isFetched: boolean;
};

/**
 * Sets the Cache Location
 * @param location
 */
export const setCacheLocation = (location: CurrentLocation) => action(SET_CACHE_LOCATION, { location });

/**
 * Reset (clear) Cache Location data
 */
const reset = () => action(RESET);

export const geolocationActions = { setCacheLocation, reset };

export type GeolocationAction = ActionType<typeof geolocationActions>;
export type GeolocationState = {
  readonly cacheLocation: CurrentLocation;
};

const LOCATION_OPTIONS = {
  timeout: 7500,
  maximumAge: 1,
  enableHighAccuracy: true
};

const FALLBACK_LOCATION_OPTIONS = {
  timeout: 7500,
  maximumAge: 1,
  enableHighAccuracy: false
};

const WATCH_LOCATION_OPTIONS = {
  maximumAge: 1,
  enableHighAccuracy: false
};

// Web (react-native-web) options. Unlike native, desktop browsers have no GPS and
// rely on the network location provider, where a high-accuracy + zero-cache request
// frequently times out or returns POSITION_UNAVAILABLE. We allow the browser to reuse
// a recently-acquired fix (generous maximumAge) and degrade to low accuracy on failure.
const WEB_LOCATION_TIMEOUT = 10000;
const WEB_LOCATION_MAX_AGE = 5 * 60 * 1000; // 5 minutes
const WEB_RETRY_DELAY_MS = 1000;
const WEB_MAX_ATTEMPTS = 3;

const webLocationOptions = (enableHighAccuracy: boolean) => ({
  timeout: WEB_LOCATION_TIMEOUT,
  maximumAge: WEB_LOCATION_MAX_AGE,
  enableHighAccuracy
});

const initialState: GeolocationState = {
  cacheLocation: {
    mocked: false,
    coords: { latitude: 0, longitude: 0, altitude: 0, speed: -1 },
    timestamp: moment().valueOf(),
    isFetched: false
  }
};

const toCurrentLocation = (data: any): CurrentLocation => {
  const altitude = data?.coords?.altitude ? data.coords.altitude : 0;
  const speed = data?.coords?.speed ? data.coords.speed : -1;

  return {
    mocked: data && data.mocked ? data.mocked : false,
    coords: {
      latitude: data.coords.latitude,
      longitude: data.coords.longitude,
      altitude,
      speed
    },
    timestamp: data.timestamp,
    isFetched: true
  };
};

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const requestBrowserPosition = (enableHighAccuracy: boolean): Promise<CurrentLocation> =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position => resolve(toCurrentLocation(position)),
      error => reject(error),
      webLocationOptions(enableHighAccuracy)
    );
  });

const getBrowserCurrentLocation = async (): Promise<CurrentLocation> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation?.getCurrentPosition) {
    throw { code: 2, message: 'Browser geolocation is not supported' };
  }

  let lastError: any = { code: 2, message: 'Position unavailable' };

  for (let attempt = 0; attempt < WEB_MAX_ATTEMPTS; attempt++) {
    // First attempt uses high accuracy for the tightest fix; subsequent attempts degrade
    // to low accuracy since desktops without GPS rarely satisfy a high-accuracy request.
    try {
      return await requestBrowserPosition(attempt === 0);
    } catch (error: any) {
      lastError = error;

      // Permission denied is never recoverable — fail fast, no retry.
      if (error?.code === 1) {
        throw error;
      }

      // kCLErrorLocationUnknown surfaces as POSITION_UNAVAILABLE (code 2); together with
      // TIMEOUT (code 3) these are transient on macOS/desktop — the CoreLocation provider
      // momentarily can't resolve a fix. Wait briefly so the OS location subsystem can
      // warm up, then retry instead of failing the whole check-in.
      if (attempt < WEB_MAX_ATTEMPTS - 1) {
        await delay(WEB_RETRY_DELAY_MS);
      }
    }
  }

  throw lastError;
};

export const getCurrentLocation = (): Promise<CurrentLocation> =>
  new Promise((resolve, reject) => {
    if (Platform.OS === 'web') {
      getBrowserCurrentLocation().then(resolve).catch(reject);
      return;
    }

    let watchIDNetwork: any;
    let watchIDGPS: any;
    let isSettled = false;

    const clearWatches = () => {
      if (watchIDNetwork !== undefined) {
        OldGeolocation.clearWatch(watchIDNetwork);
      }
      if (watchIDGPS !== undefined) {
        OldGeolocation.clearWatch(watchIDGPS);
      }
    };

    const resolveOnce = (data: any) => {
      if (isSettled) return;
      isSettled = true;
      clearWatches();
      resolve(toCurrentLocation(data));
    };

    const rejectOnce = (error: any) => {
      if (isSettled) return;
      isSettled = true;
      clearWatches();
      reject(error);
    };

    const beginWatchFallback = () => {
      const timeout = setTimeout(() => {
        rejectOnce({ code: 3, message: 'Location request timed out' });
      }, 7500);

      const handleWatchSuccess = (data: any) => {
        clearTimeout(timeout);
        resolveOnce(data);
      };

      const handleWatchError = (error: any) => {
        clearTimeout(timeout);
        rejectOnce(error);
      };

      watchIDNetwork = OldGeolocation.watchPosition(handleWatchSuccess, handleWatchError, WATCH_LOCATION_OPTIONS);

      watchIDGPS = OldGeolocation.watchPosition(handleWatchSuccess, handleWatchError, WATCH_LOCATION_OPTIONS);
    };

    Geolocation.getCurrentPosition(
      resolveOnce,
      (error: any) => {
        if (error.code === 1) {
          rejectOnce(error);
          return;
        }

        if (error.code === 3 || error.code === 5) {
          Geolocation.getCurrentPosition(
            resolveOnce,
            (fallbackError: any) => {
              if (fallbackError.code === 1) {
                if (Platform.OS === 'ios') {
                  Geolocation.requestAuthorization('whenInUse').then();
                }
                rejectOnce(fallbackError);
                return;
              }

              if (fallbackError.code === 3 || fallbackError.code === 5) {
                beginWatchFallback();
                return;
              }

              rejectOnce(fallbackError);
            },
            FALLBACK_LOCATION_OPTIONS
          );
          return;
        }

        rejectOnce(error);
      },
      LOCATION_OPTIONS
    );
  });

/**
 * Store the location from geolocation to the redux state
 */
export const updateCacheLocation = (): ThunkResult<Promise<CurrentLocation | null>> => async dispatch => {
  try {
    const location = await getCurrentLocation();
    dispatch(setCacheLocation(location));
    return location;
  } catch (error) {
    dispatch(reset());
    return null;
  }
};
export const geolocationThunk = { updateCacheLocation };

export const geolocationReducer: Reducer<GeolocationState, GeolocationAction> = (
  state = initialState,
  incomingAction
) => {
  switch (incomingAction.type) {
    case SET_CACHE_LOCATION: {
      const data = incomingAction.payload.location;
      return {
        ...state,
        cacheLocation: toCurrentLocation(data)
      };
    }
    case RESET:
      return initialState;
    default:
      return state;
  }
};
